import HeaderItemInfo from 'components/ItemLayout/HeaderItemInfo'
import getLinkToDetail from 'helpers/getLinkToDetail'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {nest, withProps} from 'recompose'
import {CustomEditorRenderer} from 'views/AppLocals/custom/CustomRichEditor'
import createPagerFooter from 'views/AppLocals/factory/createPagerFooter'
import {getFullAddress, renderIf, renderSelf} from 'views/Shared'
import EventContext from '../EventContext'
import EventNews from './EventNews'
import {isRawContentEmpty} from "../../../SlateEditor/functions/parseRawContent";
import {paths} from "../../../MainPage/contains";
import categoryIcons from "../../../../conts/categoryIcons";
import {formatDate} from "../../../../helpers/datetimeHelpers";
import {DelayRender} from "../../../Discovery/DelayRender";
import {LeafletMap} from "../../../../components/form/fields/LeafletMap";
import {getCoordinatesStore} from "../../../../helpers/localStorage";
import Coordinates from "../../../../components/Coordinates/Coordinates";
import {Avatar} from "antd";
import {BiSolidChurch} from "react-icons/bi";
import {BsPinMapFill} from "react-icons/bs";
import {ItemInformation} from "../../Organization/OrganizationEntity";


const MetaItem = ({label, value, transform = renderSelf}) =>
    renderIf(
        value,
        <Translate>
            {(t) => (
                <div className="space-x-1">
                    <span className="text-xs text-color-400 italic">
                        {`${t(label)} : `}
                    </span>
                    <span className="font-medium text-sm text-color-000">
                        {transform(value)}
                    </span>
                </div>
            )}
        </Translate>
    )

function toDateString(value) {
    return value ? formatDate(value, 'hh:mm - DD/MM/YYYY') : null
}

function toHref(value) {
    return value ? (
        <a
            href={value}
            target="_blank"
            className="flex-1 text-xs text-color-400 italic">
            {value}
        </a>
    ) : null
}

function renderIntervalWeekly(value) {
    let label = ''
    switch (value) {
        case '1000000':
            label = 'monday'
            break
        case '0100000':
            label = 'tuesday'
            break
        case '0010000':
            label = 'wednesday'
            break
        case '0001000':
            label = 'thursday'
            break
        case '0000100':
            label = 'friday'
            break
        case '0000010':
            label = 'saturday'
            break
        case '0000001':
            label = 'saturday'
            break
        default :
            label = null
            break
    }
    return label ? label : null
}

function renderInterval(value, viewOnly = false) {
    let label
    switch (value) {
        case 'D':
            label = 'daily'
            break
        case 'W':
            label = 'weekly'
            break
        case 'M':
            label = 'monthly'
            break
        default :
            if (viewOnly) return "";
            label = 'do not repeat'
            break
    }
    return (
        <Translate>
            {(t) => t(label)}
        </Translate>
    )
}


const EventInfo = () => {
    const t = useTranslate()
    const {event} = useContext(EventContext)
    const {
        about,
        time_open,
        time_close,
        categories,
        description,
        external_link,
        location_name,
        event_type,
        interval_type,
        interval_daily,
    } = event ?? {}
    const {latitude, longitude} = getCoordinatesStore();

    const coordinates = _.get(event, 'event_locations', []).map((e, i) => {
        return {
            name: (
                <div key={i}
                     className="flex gap-2 rounded-lg custom-item-render render-border-center hover:shadow relative">
                    <Avatar
                        size={66}
                        className="flex flex-center background-200 rounded-l-lg rounded-r-0 cursor-pointer"
                        src={e?.church?.photo}
                        icon={e?.church ? <BiSolidChurch size={60} className="text-color-500"/> :
                            <BsPinMapFill/>}
                    />
                    <div className="flex flex-col flex-1 gap-1">
                        <div
                            className="font-normal text-sm text-color-000 hover:text-primary max-lines-2 leading-tight cursor-pointer">
                            {e?.name}
                        </div>
                        <div className="flex flex-col">
                            {
                                e?.church ? (
                                    <ItemInformation label="address" value={getFullAddress(e?.church)}
                                                     className="text-xs"/>
                                ) : (
                                    <ItemInformation label="address" value={e?.address} className="text-xs"/>
                                )
                            }
                        </div>
                    </div>
                </div>
            ),
            longitude: Number(e?.longitude),
            latitude: Number(e?.latitude),
        }
    });

    return (
        <div className="flex flex-col items-start space-y-6">
            <HeaderItemInfo title="detail">
                <div className="space-y-2 mb-2">
                    {renderIf(
                        description,
                        <span className="text-sm text-color-400 italic whitespace-pre-line">{description}</span>
                    )}
                    {[
                        {
                            label: 'type of event',
                            value: t(event_type),
                        },
                        {
                            label: 'location',
                            value: location_name,
                        },
                        {
                            label: 'link',
                            value: external_link,
                            transform: toHref,
                        },
                        {
                            label: 'start',
                            value: time_open,
                            transform: (value) => {
                                let label = ''
                                if (interval_type === 'D') {
                                    label = formatDate(value, 'HH:mm')
                                } else if (interval_type === 'W') {
                                    label = [
                                        formatDate(value, 'HH:mm'),
                                        t(renderIntervalWeekly(interval_daily))
                                    ].join(' - ')
                                } else if (interval_type === 'M') {
                                    label = [
                                        formatDate(value, 'HH:mm'),
                                        '-',
                                        t('date'),
                                        formatDate(value, 'DD')
                                    ].join(' ')
                                } else {
                                    label = formatDate(value, 'HH:mm - DD/MM/YYYY')
                                }
                                return (
                                    <span>
                                        {label}{' '}
                                        {interval_type !== 'N' && <span className="text-color-300 italic">({renderInterval(interval_type, true)})</span>}
                                    </span>
                                )
                            },
                        },
                        {
                            label: 'end',
                            value: time_close,
                            transform: toDateString,
                        },
                    ].map(({label, value, transform}, index) => (
                        <MetaItem
                            key={index}
                            {...{
                                label,
                                value,
                                transform,
                            }}
                        />
                    ))}
                </div>
                <div className="px-3 text-right">
                    <Link
                        to={getLinkToDetail(event) + '/timeline'}
                        className="font-medium text-xs text-color-400 italic tracking-wider no-underline">
                        {t('see more')}
                        {' ...'}
                    </Link>
                </div>
            </HeaderItemInfo>

            {coordinates?.length > 0 ? (
                <DelayRender lazy={true} time={500}>
                    <LeafletMap
                        fullScreenAllow={true}
                        style={{
                            height: '50vh',
                            width: '100%'
                        }}
                        zoom={12}
                        zoomControl={false}
                        coordinates={coordinates}
                        longitude={coordinates[0]?.longitude ?? 0}
                        latitude={coordinates[0]?.latitude ?? 0}
                    />
                </DelayRender>
            ) : null}

            <DelayRender lazy={true} time={1000}>
                {!isRawContentEmpty(about) && (
                    <CustomEditorRenderer
                        rawValue={about}
                        Wrapper={withProps({
                            className: 'border-transparent py-2',
                            title: 'about event',
                        })(HeaderItemInfo)}
                    />
                )}
            </DelayRender>


            <HeaderItemInfo
                title="website"
                hidden={_.isEmpty(external_link)}>
                <Link
                    target="_blank"
                    to={external_link ?? '#'}
                    className="flex-1 text-xs text-color-400 italic">
                    {external_link}
                </Link>
            </HeaderItemInfo>

            <HeaderItemInfo
                title="categories"
                className="flex flex-wrap gap-3"
                hidden={_.isEmpty(categories)}>
                {Array.from(categories ?? []).map((e, i) => {
                    const {name, language, alt_idname} = e
                    const id = e?.id
                    const pathname = paths.categoryPath.replace(':category', id)
                    const Icon = _.get(categoryIcons, e.idname, categoryIcons["default"])

                    return (
                        <Link
                            key={i}
                            to={{
                                pathname,
                                search: ``,
                                state: {refId: alt_idname},
                            }}
                            className="w-fit background-100 hover:bg-primary-50 rounded-md text-color-000 hover:text-primary no-underline truncate">
                            <div style={{padding: '5px 12px'}} className="flex flex-center gap-3">
                                <Icon size={20}/>
                                <span className="font-medium pr-2">{name}</span>
                            </div>
                        </Link>
                    )
                })}
            </HeaderItemInfo>

            <DelayRender lazy={true} time={1500}>
                <EventNews
                    sample={2}
                    Footer={withProps({
                        pathname: getLinkToDetail(event) + '/news',
                    })(({pathname}) =>
                        createPagerFooter({
                            to: pathname,
                            description:
                                'You can click "See more" to see this full information',
                        })
                    )}
                    Wrapper={withProps({
                        title: 'news update',
                    })(HeaderItemInfo)}
                />
            </DelayRender>
        </div>
    )
}

export default nest(
    Coordinates,
    EventInfo
)
